import { z } from 'zod';
import { daysBetweenInclusive } from './absence.js';
import { UsdAmount } from './ai.js';
import { IssueKind } from './issue.js';
import { CalendarDate, ProjectPriority, ProjectStatus } from './project.js';
/**
 * Báo cáo công việc của team — APM-TWR,
 * [docs/plan-team-work-report.md](../../../docs/plan-team-work-report.md).
 *
 * ## Hai phần, sống khác nhau (plan §2)
 *
 * - `TeamWorkFacts`: bảng số liệu do **code** tính lúc tạo. Có cả khi không có
 *   key AI, và là ảnh chụp: không bao giờ sửa, kể cả khi dữ liệu gốc đổi sau đó.
 * - `TeamWorkNarrative`: lời văn do AI viết nháp hoặc leader tự viết. Sửa được
 *   khi còn là nháp.
 *
 * ## ⚠️ Không chấm điểm, không xếp hạng (plan §2.2, spec mục 14)
 *
 * Không schema nào ở đây có trường `score`/`rating`/`overall` hay bất kỳ tên
 * nào cùng nghĩa. Báo cáo trả lời "đã làm được gì", không trả lời "làm tốt hay
 * kém". `team-report.spec.ts` quét **mọi** schema của file này để canh luật ấy.
 */
/** Mức chi tiết của lời văn — plan §2.1. Bảng số liệu luôn đầy đủ ở mọi mức. */
export const TeamReportDetail = z.enum(['low', 'medium', 'high']);
/** Nháp → chốt. Bản đã chốt không sửa được nữa. */
export const TeamReportStatus = z.enum(['draft', 'finalized']);
/** `ai_edited` = người sửa một lời văn mà trước đó AI viết. */
export const TeamReportNarrativeSource = z.enum(['ai', 'manual', 'ai_edited']);
/**
 * Vai của người đang xem — plan §5. Quyết định payload bị cắt tới đâu.
 *
 * - `owner`: người tạo. Xem toàn bộ, cả nháp.
 * - `superior`: cấp trên có quyền hiệu suất trên mọi thành viên. Chỉ bản đã chốt.
 * - `member`: một người có mặt trong báo cáo. Chỉ bản đã chốt, và **chỉ phần của
 *   chính mình**.
 */
export const TeamReportViewerRole = z.enum(['owner', 'superior', 'member']);
// ── Trần — plan §3.1 ────────────────────────────────────────────────────────
/** Độ dài kỳ tối đa, tính cả hai đầu. 367 ngày là bị từ chối. */
export const MAX_TEAM_REPORT_DAYS = 366;
/** Cấp dưới trực tiếp tối đa trong một báo cáo. Chạm trần thì 400, không cắt. */
export const MAX_TEAM_REPORT_MEMBERS = 100;
/** Mỗi người × dự án: tối đa bấy nhiêu task **và** bấy nhiêu issue. Vượt thì cắt, `truncated = true`. */
export const MAX_TEAM_REPORT_ITEMS = 200;
/** Trần kích thước `facts` sau khi serialize. Vượt thì cắt tiếp, cũ nhất trước. */
export const MAX_TEAM_REPORT_FACTS_BYTES = 2 * 1024 * 1024;
/** Mô tả task ở mức `high` cắt còn bấy nhiêu ký tự. */
export const TEAM_REPORT_DESCRIPTION_CHARS = 300;
/** Trần cứng một đoạn lời văn. Độ dài theo mức chi tiết do skill và prompt quyết định. */
export const MAX_TEAM_REPORT_TEXT_CHARS = 4000;
/** Phần tóm tắt trên cùng (APM-TWR-i): tối đa bấy nhiêu gạch mỗi dự án. */
export const MAX_TEAM_REPORT_SUMMARY_BULLETS = 50;
/** Phần tóm tắt trên cùng (APM-TWR-i): trần một gạch. */
export const MAX_TEAM_REPORT_SUMMARY_BULLET_CHARS = 500;
/**
 * Mục checklist đã xong mỗi task trong `facts` — APM-TWR-g. Vượt thì giữ mới
 * nhất và đặt `facts.truncated`. Checklist tối đa 100 mục, nên trần này chỉ chạm
 * khi một người tick hơn nửa một checklist rất dài trong cùng một kỳ.
 */
export const MAX_TEAM_REPORT_CHECKLIST_ITEMS = 50;
/** Mức `medium`: model đọc tối đa bấy nhiêu mục checklist mỗi task (mới nhất). */
export const TEAM_REPORT_MEDIUM_CHECKLIST_ITEMS = 10;
// ── Số liệu (ảnh chụp) ─────────────────────────────────────────────────────
/**
 * Một mục checklist **đi từ chưa tick sang tick trong kỳ**, và vẫn tick ở cuối
 * kỳ — APM-TWR-g. Tính cho **người tick** (`actor` của event), không cho người
 * được giao. `weight` chỉ có khi mục ấy được đặt trọng số tường minh.
 */
export const TeamWorkChecklistDone = z.object({
    text: z.string(),
    weight: z.number().positive().max(100).optional(),
    checkedAt: z.iso.datetime(),
});
const ProgressPct = z.number().int().min(0).max(100);
/**
 * Tiến độ trong kỳ và checklist đã xong — APM-TWR-g, chung cho task xong và
 * task đang làm.
 *
 * Mọi trường **tuỳ chọn**: ảnh chụp chụp trước APM-TWR-g không có chúng và vẫn
 * phải mở được (ảnh chụp không bao giờ tính lại). Vắng `progressFrom` nghĩa là
 * "không dựng lại được tiến độ", không phải "0%".
 *
 * `progressDelta` có thể **âm** (việc bị kéo lùi) — đó là thông tin thật, không
 * kẹp về 0.
 */
const TaskPeriodFields = {
    /** Tiến độ tại đầu kỳ, dựng từ `event_log`. */
    progressFrom: ProgressPct.optional(),
    /** Tiến độ tại cuối kỳ. Task xong trong kỳ luôn là 100. */
    progressTo: ProgressPct.optional(),
    /** `progressTo − progressFrom`, có thể âm. */
    progressDelta: z.number().int().min(-100).max(100).optional(),
    /** Mục checklist **người này** tick xong trong kỳ, cũ nhất trước. */
    checklistDone: z.array(TeamWorkChecklistDone).max(MAX_TEAM_REPORT_CHECKLIST_ITEMS).optional(),
};
/** Một task đã xong trong kỳ. `description` chỉ có ở mức `high`, vắng hẳn ở hai mức kia. */
export const TeamWorkTaskDone = z.object({
    taskId: z.uuid(),
    /** Mã hiển thị, vd `APM-12`. */
    key: z.string(),
    title: z.string(),
    completedAt: z.iso.datetime(),
    /** `null` = task không có ước lượng — vẫn là việc đã làm, không được biến mất. */
    estimateHours: z.number().nonnegative().nullable(),
    /**
     * **Mọi** giờ người được ghi nhận đã bấm trên task này tới cuối kỳ, không cắt
     * đầu kỳ — để so được với `estimateHours`. Vì thế **không** là một phần của
     * `TeamWorkPersonFacts.hours` (APM-TWR-b).
     */
    workedHours: z.number().nonnegative(),
    description: z.string().optional(),
    ...TaskPeriodFields,
});
/**
 * Task tới cuối kỳ chưa xong mà người này có động tới trong kỳ: bấm giờ, được
 * giao và tiến độ tăng, hoặc tick checklist (APM-TWR-g) — kể cả khi không bấm
 * giờ, nên `hours` có thể là 0.
 */
export const TeamWorkTaskOngoing = z.object({
    taskId: z.uuid(),
    key: z.string(),
    title: z.string(),
    statusName: z.string(),
    /** Giờ **trong kỳ** của người này trên task này. */
    hours: z.number().nonnegative(),
    ...TaskPeriodFields,
});
export const TeamWorkIssueResolved = z.object({
    issueId: z.uuid(),
    key: z.string(),
    title: z.string(),
    kind: IssueKind,
    resolvedAt: z.iso.datetime(),
    /** Cùng nghĩa với `TeamWorkTaskDone.workedHours`: mọi giờ của người được giao tới cuối kỳ. */
    hours: z.number().nonnegative(),
});
/** Việc của một người ở một dự án. */
export const TeamWorkPersonFacts = z.object({
    userId: z.uuid(),
    /** Giờ bấm **trong kỳ** ở dự án này, cắt theo cửa sổ — khớp timesheet cùng kỳ. */
    hours: z.number().nonnegative(),
    tasksDone: z.array(TeamWorkTaskDone),
    tasksOngoing: z.array(TeamWorkTaskOngoing),
    issuesResolved: z.array(TeamWorkIssueResolved),
    /** Ghi chú bấm giờ (`work_session.note`). Chỉ có ở mức `high`. */
    notes: z.array(z.string()).optional(),
});
/**
 * Số liệu tổng của một thành viên trong kỳ — APM-TWR-k. Bảng tổng trên đầu tab
 * "Theo người".
 *
 * - `tasksDone`/`issuesResolved` đếm **trước** khi cắt danh sách (plan §3.1):
 *   cắt bớt dòng không được làm sai con số.
 * - `loggedHours`: giờ bấm trong kỳ, mọi dự án có trong báo cáo (dự án demo bị
 *   loại như phần còn lại) — timesheet không có bảng khai riêng, nó dựng từ
 *   `work_session`.
 * - Workload = giờ ước lượng được trải lên các ngày trong kỳ / trần năng lực,
 *   cùng cách trải với `WorkloadService`. Việc **đã xong trong kỳ** vẫn tính —
 *   không thì workload của một tuần đã qua chỉ còn phần việc chưa xong.
 * - "Ngày trọn" = ngày có trần năng lực bằng ngày dài nhất trong lịch tuần của
 *   người đó (người dùng chốt 2026-09-28): thứ 7 làm nửa buổi, nghỉ phép nửa
 *   ngày, ngày lễ đều **không** phải ngày trọn. Giờ bấm vào những ngày ấy có
 *   trong `loggedHours` nhưng không vào trung bình.
 *
 * `null` = mẫu số bằng 0, không phải 0.
 */
export const TeamWorkMemberStats = z.object({
    tasksDone: z.number().int().nonnegative(),
    issuesResolved: z.number().int().nonnegative(),
    loggedHours: z.number().nonnegative(),
    committedHours: z.number().nonnegative(),
    capacityHours: z.number().nonnegative(),
    workloadPct: z.number().int().nonnegative().nullable(),
    fullDays: z.number().int().nonnegative(),
    fullDayHours: z.number().nonnegative(),
    avgHoursPerFullDay: z.number().nonnegative().nullable(),
});
export const TeamWorkMember = z.object({
    userId: z.uuid(),
    name: z.string(),
    /** Chỉ ở `facts.members`, và tuỳ chọn — ảnh chụp trước APM-TWR-k không có. */
    stats: TeamWorkMemberStats.optional(),
});
/** Trần `TeamWorkProjectOverview.members` — dự án đông hơn thì cắt và đặt `facts.truncated`. */
export const MAX_TEAM_REPORT_PROJECT_MEMBERS = 200;
/**
 * Tổng quan một dự án **lúc chụp** — APM-TWR-j. Một hàng ô số trên đầu mỗi dự
 * án ở tab "Theo dự án", để đọc lướt mà không phải mở lời văn.
 *
 * % hoàn thành = task **gốc** đã xong / tổng task gốc, cùng công thức với thẻ
 * sức khoẻ dự án (`PortfolioQueryService.health()`). "Hiện tại" là **cuối kỳ**
 * (không phải lúc bấm tạo), "tuần trước" là cuối kỳ − 7 ngày. `null` = mốc ấy
 * chưa có task nào, không phải 0%.
 *
 * Không có ở ảnh chụp trước TWR-j, và thành viên (`viewerRole = member`) không
 * nhận: danh sách thành viên dự án là thông tin về đồng nghiệp.
 */
export const TeamWorkProjectOverview = z.object({
    status: ProjectStatus,
    /** APM-PSET-prio — ảnh chụp trước đó không có. */
    priority: ProjectPriority.optional(),
    startDate: CalendarDate.nullable(),
    endDate: CalendarDate.nullable(),
    /** Người giữ vai `PM`, xếp theo tên. */
    pms: z.array(TeamWorkMember),
    /** **Mọi** thành viên dự án lúc chụp, không chỉ người trong team. Số người = độ dài mảng. */
    members: z.array(TeamWorkMember).max(MAX_TEAM_REPORT_PROJECT_MEMBERS),
    taskCount: z.number().int().nonnegative(),
    doneCount: z.number().int().nonnegative(),
    progressPct: ProgressPct.nullable(),
    taskCountWeekAgo: z.number().int().nonnegative(),
    doneCountWeekAgo: z.number().int().nonnegative(),
    progressPctWeekAgo: ProgressPct.nullable(),
    /** `progressPct − progressPctWeekAgo`, điểm %, có thể âm. `null` khi một mốc không có số. */
    progressDelta: z.number().int().min(-100).max(100).nullable(),
});
export const TeamWorkProjectFacts = z.object({
    projectId: z.uuid(),
    name: z.string(),
    /** Tổng giờ của **những người có trong `people`** ở dự án này. */
    hours: z.number().nonnegative(),
    people: z.array(TeamWorkPersonFacts),
    /** Tuỳ chọn — ảnh chụp trước APM-TWR-j không có. */
    overview: TeamWorkProjectOverview.optional(),
});
/**
 * Bảng số liệu của cả báo cáo — ảnh chụp tại `capturedAt`.
 *
 * `from`/`to` là ngày thuần theo `timezone` (múi giờ tổ chức lúc chụp), `to`
 * tính **trọn ngày**. Ghi lại `timezone` vì tổ chức có thể đổi múi giờ về sau,
 * và khi ấy người đọc vẫn phải biết kỳ này đã được cắt theo múi nào.
 *
 * `truncated = true` khi chạm một trần ở plan §3.1. Cắt thì phải **nói ra** —
 * cắt im lặng rơi đúng vào màn hình dùng để nhìn việc của con người.
 */
export const TeamWorkFacts = z.object({
    capturedAt: z.iso.datetime(),
    timezone: z.string(),
    from: CalendarDate,
    to: CalendarDate,
    /** Mọi cấp dưới trực tiếp lúc chụp, kể cả người không có hoạt động nào trong kỳ. */
    members: z.array(TeamWorkMember),
    projects: z.array(TeamWorkProjectFacts),
    truncated: z.boolean(),
});
// ── Lời văn ─────────────────────────────────────────────────────────────────
/** ⚠️ `.trim()` **trước** `.min()` — xem cạm bẫy ở CLAUDE.md. */
const NarrativeText = z.string().trim().min(1).max(MAX_TEAM_REPORT_TEXT_CHARS);
/**
 * Tham chiếu tới một hạng mục trong `facts`. `finalize()` của skill (TWR-d) và
 * server (TWR-c) kiểm `id` phải có trong ảnh chụp — model chỉ được diễn đạt
 * những gì đã có, không được bịa việc.
 */
export const TeamReportRef = z.object({
    type: z.enum(['task', 'issue']),
    id: z.uuid(),
});
const Refs = z.array(TeamReportRef).max(MAX_TEAM_REPORT_ITEMS * 2);
export const TeamWorkProjectNarrative = z.object({
    projectId: z.uuid(),
    summary: NarrativeText,
    highlights: z.array(z.string().trim().min(1).max(500)).max(50),
    refs: Refs,
});
export const TeamWorkPersonProjectNarrative = z.object({
    projectId: z.uuid(),
    summary: NarrativeText,
    refs: Refs,
});
export const TeamWorkPersonNarrative = z.object({
    userId: z.uuid(),
    overview: NarrativeText,
    projects: z.array(TeamWorkPersonProjectNarrative).max(500),
});
/**
 * Một mục của phần tóm tắt **trên cùng** — một dự án. Không chia theo người, và
 * **không con số tiến độ** (luật ở `draftSummary` và prompt của skill). Tên dự
 * án không nằm trong chữ: giao diện in đậm nó ở đầu mục.
 *
 * - `bullets` (APM-TWR-i): **mỗi việc một gạch** — task hoặc issue. Việc dở có
 *   checklist là một gạch dạng "<tiêu đề>: xong <mục a>, <mục b>".
 * - `text` (APM-TWR-h): đoạn văn liền của bản lưu trước APM-TWR-i. **Chỉ còn
 *   để đọc** — bản nháp cũ nằm nguyên trong cột JSON và phải parse được; bản
 *   viết mới (trình sửa, skill) chỉ ghi `bullets`. Giao diện hiện `bullets` khi
 *   có, không thì hiện `text` như một đoạn.
 *
 * Mục phải có ít nhất một trong hai — một mục rỗng là một tên dự án in đậm
 * đứng trơ trọi.
 */
export const TeamWorkSummaryParagraph = z
    .object({
    projectId: z.uuid(),
    bullets: z
        .array(z.string().trim().min(1).max(MAX_TEAM_REPORT_SUMMARY_BULLET_CHARS))
        .max(MAX_TEAM_REPORT_SUMMARY_BULLETS)
        .default([]),
    refs: Refs,
    text: NarrativeText.optional(),
})
    .refine((entry) => entry.bullets.length > 0 || entry.text !== undefined, {
    message: 'Mục tóm tắt cần ít nhất một gạch.',
    path: ['bullets'],
});
/**
 * Lời văn của cả báo cáo — output của skill `summarize_team_work` và body của
 * `PATCH /team-reports/:id/narrative`.
 *
 * Mảng được phép **thiếu** dự án hay người: leader tự viết có thể chỉ viết vài
 * đoạn. Đoạn nào đã có thì không được rỗng.
 *
 * `summary` (APM-TWR-h, gạch đầu dòng từ APM-TWR-i) là phần tóm tắt trên cùng, mỗi dự án có việc một mục,
 * theo thứ tự `facts.projects`. **Tuỳ chọn**: lời văn lưu trước APM-TWR-h không
 * có nó, và khi ấy giao diện hiện `overview` như cũ. `overview` vẫn bắt buộc —
 * bản cũ phải parse được, và nó là đoạn duy nhất khi team không có việc nào.
 */
export const TeamWorkNarrative = z.object({
    /** Một đoạn cho cả team. Có `summary` thì giao diện hiện `summary` thay cho nó. */
    overview: NarrativeText,
    summary: z.array(TeamWorkSummaryParagraph).max(500).optional(),
    projects: z.array(TeamWorkProjectNarrative).max(500),
    people: z.array(TeamWorkPersonNarrative).max(MAX_TEAM_REPORT_MEMBERS),
});
/**
 * Phần lời văn một **thành viên** được xem: chỉ `people[me]`.
 *
 * Không có `overview`, `summary` và `projects`: cả ba kể việc của đồng nghiệp. Server cắt
 * payload trước khi trả về (TWR-c), không để FE tự ẩn — nên hình dạng này là
 * một schema riêng, không phải `TeamWorkNarrative` với vài trường để trống.
 */
export const TeamWorkMemberNarrative = z.object({
    people: z.array(TeamWorkPersonNarrative).max(1),
});
// ── Input ───────────────────────────────────────────────────────────────────
/**
 * Tạo báo cáo. Team luôn là cấp dưới trực tiếp của **người gọi** — không có
 * trường chọn người, để không ai tạo được báo cáo về người không thuộc quyền mình.
 *
 * `to ≤ hôm nay` **không** kiểm ở đây: "hôm nay" theo múi giờ tổ chức, và chỉ
 * server biết múi ấy (`OrgTimezoneService`). Trình duyệt ở múi khác sẽ tính sai.
 */
export const CreateTeamReportInput = z
    .object({
    from: CalendarDate,
    to: CalendarDate,
    detail: TeamReportDetail,
    /** Yêu cầu thêm cho AI (tuỳ chọn). Lưu lại, vì lời văn có thể được sinh sau. */
    note: z.string().trim().max(1000).optional(),
    /**
     * Làm thay cho leader này — APM-TWRD-b. Có ⇒ người gọi phải đang được leader
     * ấy ủy quyền (server kiểm, không thì 404); team của báo cáo là team của
     * **leader**, không của người gọi. Bỏ trống = báo cáo của chính mình.
     */
    onBehalfOf: z.uuid().optional(),
})
    .superRefine((input, ctx) => {
    if (input.to < input.from) {
        ctx.addIssue({
            code: 'custom',
            path: ['to'],
            message: 'Ngày kết thúc phải sau hoặc bằng ngày bắt đầu',
        });
        return;
    }
    if (daysBetweenInclusive(input.from, input.to) > MAX_TEAM_REPORT_DAYS) {
        ctx.addIssue({
            code: 'custom',
            path: ['to'],
            message: `Kỳ báo cáo không dài quá ${MAX_TEAM_REPORT_DAYS} ngày`,
        });
    }
});
/**
 * Sửa lời văn của một bản nháp. Không có trường `source`: server tự suy ra
 * (`manual` khi chưa có lời hoặc lời cũ là `manual`, `ai_edited` khi lời cũ là
 * `ai`) — để client không tự nhận một đoạn đã sửa tay là nguyên văn của AI.
 */
export const UpdateTeamReportNarrativeInput = z.object({
    narrative: TeamWorkNarrative,
});
/**
 * `POST /team-reports/:id/narrative/generate` — AI viết nháp lời văn (APM-TWR-d).
 *
 * `prompt` bỏ trống thì dùng `note` đã lưu lúc tạo báo cáo: yêu cầu thêm của
 * leader đi theo bản ấy, không bắt gõ lại mỗi lần sinh. `runId` do client sinh
 * (`randomUuid()`) rồi mở SSE theo dõi **trước** khi POST, như mọi nút AI khác.
 */
export const GenerateTeamReportNarrativeInput = z.object({
    prompt: z.string().trim().max(1000).optional(),
    runId: z.uuid().optional(),
});
// ── Ủy quyền làm thay — APM-TWRD ────────────────────────────────────────────
/**
 * `PUT /team-reports/delegation` — leader ủy quyền cho **một** cấp dưới trực
 * tiếp làm thay báo cáo công việc team (người dùng chốt 2026-09-30).
 *
 * `endsAt` bỏ trống = tới khi thu hồi; có thì là **ngày cuối** còn hiệu lực,
 * theo ngày của tổ chức — server kiểm `endsAt ≥ hôm nay` (chỉ server biết múi
 * giờ ấy). Cấp cho người mới là thu hồi người cũ trong cùng lệnh.
 */
export const GrantTeamReportDelegationInput = z.object({
    delegateId: z.uuid(),
    endsAt: CalendarDate.optional(),
});
/** Một lần ủy quyền. `active` do server tính **lúc đọc** — xem `TeamReportDelegationService`. */
export const TeamReportDelegation = z.object({
    id: z.uuid(),
    managerId: z.uuid(),
    managerName: z.string(),
    delegateId: z.uuid(),
    delegateName: z.string(),
    endsAt: CalendarDate.nullable(),
    createdAt: z.iso.datetime(),
    revokedAt: z.iso.datetime().nullable(),
    active: z.boolean(),
});
/**
 * `GET /team-reports/delegation` — hai chiều của người gọi.
 *
 * - `outgoing`: người mình đang ủy quyền (mình là leader). `null` = không ai.
 * - `incoming`: leader đang ủy quyền cho mình. `null` = không làm thay ai.
 * - `history`: các lần mình từng ủy quyền, mới nhất trước (≤ 20). Chỉ tên và
 *   ngày — không số liệu của ai.
 */
export const TeamReportDelegationState = z.object({
    outgoing: TeamReportDelegation.nullable(),
    incoming: TeamReportDelegation.nullable(),
    history: z.array(TeamReportDelegation),
    /**
     * AI của **leader** dùng được không, khi mình đang làm thay — APM-TWRD-c/d.
     * Nút AI của người làm thay hỏi cái này chứ không hỏi `GET /ai/capabilities`
     * của chính mình: AI chạy bằng nguồn của leader, và người làm thay thường
     * không có key riêng. `null` khi không làm thay ai.
     */
    incomingAi: z.object({ enabled: z.boolean(), message: z.string().nullable() }).nullable(),
});
/** `GET /team-reports?scope=` — ba mục của trang danh sách (plan §7). */
export const TeamReportListScope = z.enum(['mine', 'aboutMe', 'managed']);
export const TeamReportListQuery = z.object({
    scope: TeamReportListScope.default('mine'),
});
// ── Output ──────────────────────────────────────────────────────────────────
/** Một dòng trong danh sách. Không mang `facts` hay lời văn. */
export const TeamReportSummary = z.object({
    id: z.uuid(),
    managerId: z.uuid(),
    managerName: z.string(),
    /** Ai soạn thay — APM-TWRD-b. `null` = leader tự soạn. */
    delegateId: z.uuid().nullable(),
    delegateName: z.string().nullable(),
    from: CalendarDate,
    to: CalendarDate,
    detail: TeamReportDetail,
    status: TeamReportStatus,
    narrativeSource: TeamReportNarrativeSource.nullable(),
    memberCount: z.number().int().nonnegative(),
    truncated: z.boolean(),
    createdAt: z.iso.datetime(),
    finalizedAt: z.iso.datetime().nullable(),
});
export const TeamReportList = z.object({
    items: z.array(TeamReportSummary),
});
const TeamReportViewBase = TeamReportSummary.omit({ memberCount: true }).extend({
    updatedAt: z.iso.datetime(),
});
/**
 * Người tạo và cấp trên: toàn bộ báo cáo.
 *
 * `note`, `aiRunId`, `costUsd` chỉ có ở nhánh này — yêu cầu thêm của leader và
 * chi phí AI không phải việc của người được báo cáo về.
 */
export const TeamReportFullView = TeamReportViewBase.extend({
    viewerRole: TeamReportViewerRole.extract(['owner', 'superior']),
    /**
     * Người xem sửa / chốt / bỏ / chạy AI được bản nháp này không — leader, hoặc
     * người **đang** được leader ủy quyền (APM-TWRD-b). Người từng soạn thay mà
     * đã hết ủy quyền vẫn là `owner` (xem được bản mình soạn) nhưng `false`.
     */
    canEdit: z.boolean(),
    note: z.string().nullable(),
    facts: TeamWorkFacts,
    narrative: TeamWorkNarrative.nullable(),
    aiRunId: z.uuid().nullable(),
    costUsd: UsdAmount,
});
/**
 * Thành viên: chỉ phần của chính mình.
 *
 * `facts` cùng hình dạng nhưng server đã cắt (TWR-c): `members` chỉ còn mình,
 * `projects` chỉ còn dự án mình có việc, `people` chỉ còn mình, và `hours` của
 * dự án là **giờ của mình** — tổng của cả team cũng là thông tin về đồng nghiệp.
 */
export const TeamReportMemberView = TeamReportViewBase.extend({
    viewerRole: TeamReportViewerRole.extract(['member']),
    facts: TeamWorkFacts,
    narrative: TeamWorkMemberNarrative.nullable(),
});
/** `GET /team-reports/:id` — phân nhánh theo `viewerRole`, FE thu hẹp kiểu bằng trường này. */
export const TeamReportView = z.discriminatedUnion('viewerRole', [
    TeamReportFullView,
    TeamReportMemberView,
]);
/**
 * Kết quả sinh lời văn. Lời văn đã được ghi vào bản nháp (`narrativeSource =
 * 'ai'`); FE tải lại `GET /team-reports/:id` để đọc nó.
 *
 * `unverified` là những gì model nhắc mà **không có** trong ảnh chụp số liệu
 * (một task, một dự án, một người lạ) — đã bị bỏ khỏi lời văn trước khi ghi.
 * Nói ra để leader biết đoạn nào có thể hụt một tham chiếu. `partial = true`
 * khi lượt chạy chạm trần và lời văn là phần model kịp viết.
 */
export const GenerateTeamReportNarrativeResult = z.object({
    report: TeamReportSummary,
    unverified: z.array(z.object({ path: z.string(), reason: z.string() })),
    partial: z.boolean(),
    /** Chi phí **của lượt này**; tổng cộng dồn nằm ở `TeamReportFullView.costUsd`. */
    costUsd: UsdAmount,
});
//# sourceMappingURL=team-report.js.map